import React, { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { useEditor } from "@tiptap/react"
import Split from "react-split"
import { ConfigProvider, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faHardDrive, faIndent } from "@fortawesome/free-solid-svg-icons"

import Document from "@tiptap/extension-document"
import Text from "@tiptap/extension-text"
import Paragraph from "@tiptap/extension-paragraph"
import History from "@tiptap/extension-history"
import Placeholder from "@tiptap/extension-placeholder"
import { CommandSuggestion } from "features/QueryConsole/components/Extensions/CommandSuggestionExtension"
import QuerySyntaxHighlight from "features/QueryConsole/components/Extensions/QuerySyntaxHighlightingExtension"
import QueryIndent from "features/QueryConsole/components/Extensions/QueryIndentExtension"
import SmartClipboardExtension from "shared/TipTap/Extensions/SmartClipboardExtension"

import { useProjectStore, Project } from "stores/projectStore"
import { useQueryConsoleLayoutStore } from "stores/queryConsoleLayoutStore"
import {
  useBatchBuilderScript,
  useBuilderScript,
  useQueryBuilderStore,
  buildTransformPrefix,
} from "stores/queryBuilderStore"
import { exportToExcel } from "utils/exportToExcel"
import { RTableContent } from "types/engineOutput.types"
import { useTransformBackend } from "features/QueryConsole/hooks/useTransformBackend"

import Console from "features/QueryConsole/components/Console.component"
import QueryStatus from "features/QueryConsole/components/QueryStatus.component"
import QueryConsoleActions from "features/QueryConsole/components/QueryConsoleActions.component"
import QueryConsoleViewMode from "features/QueryConsole/components/QueryConsoleViewMode.component"
import QueryConsoleLayoutControls from "features/QueryConsole/components/QueryConsoleLayoutControls.component"
import QueryConsoleHelp from "features/QueryConsole/components/QueryConsoleHelp.component"
import QueryBuilder from "features/QueryConsole/components/Builder/QueryBuilder.component"
import TransformView from "features/QueryConsole/components/Transform/TransformView.component"
import DataTree from "features/QueryConsole/components/DataTree.component"
import SavedQueries from "features/QueryConsole/components/SavedQueries/SavedQueries.component"
import EmptyState from "shared/ui/EmptyState/EmptyState.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import { notify } from "shared/ui/Notifications/notify"

import { useEngine } from "hooks/api/useEngine"
import { useSavedQueries } from "features/QueryConsole/api/useSavedQueries"
import { useDataImport } from "features/DataImport/api/useDataImport"
import { useAsync } from "hooks/core/useAsync"
import handleSequenceError from "utils/handleSequenceError"
import { QueryInstance } from "types/engine.types"
import { DATAFILE_TYPE } from "types/dataImport.types"
import { CodebookData } from "types/codebook.types"
import { EngineQueryResult } from "types/engineOutput.types"
import { AxiosResponse } from "axios"

import "styles/queryConsole.css"

declare global {
  interface Window {
    RunEngineQuery: (
      instanceID: QueryInstance,
      query: string,
      isAsync: boolean
    ) => Promise<AxiosResponse<unknown>>
    queryInstance: QueryInstance | null
  }
}

interface QueryConsoleContainerProps {
  activeKey?: string
}

// Tab key of the "Data Reporting" tab in ProjectDetailsTabs. Used to detect
// the user returning to this tab so we can check for re-imported data.
const DATA_REPORTING_TAB_KEY = "5"

const QC_THEME = {
  token: {
    colorPrimary: "#2BA8CF",
    colorText: "#1A1817",
    colorTextSecondary: "#5C544B",
    colorBgContainer: "#FFFFFF",
    borderRadius: 4,
    fontFamily: '"General Sans", system-ui, sans-serif',
    // Tinted accent for menu/dropdown hover and selected states. Default
    // tokens give a flat grey/black hover that fights the warm-paper theme;
    // these tints keep hover legible and on-brand.
    controlItemBgHover: "rgba(43, 168, 207, 0.10)",
    controlItemBgActive: "rgba(43, 168, 207, 0.18)",
    controlItemBgActiveHover: "rgba(43, 168, 207, 0.22)",
  },
}

/**
 * Container component for the Query Console.
 * Manages the workspace where users write, run, and save Data(Q) queries.
 *
 * Features:
 * - Three view modes (Builder placeholder, Console editor, Saved queries) via the tab ribbon.
 * - Persistent layout: hide/show DataTree, toggle vertical/horizontal split, swap editor/output order.
 * - Real-time script execution status with async polling.
 * - Codebook-driven variable tree.
 *
 * @component
 */
const QueryConsoleContainer: React.FC<QueryConsoleContainerProps> = ({
  activeKey,
}) => {
  const project: Project = useProjectStore((state) => state.project)
  const {
    initialiseData,
    isInitialisingData,
    RunEngineQuery,
    isRunningEngineQuery,
  } = useEngine()
  const { getDataFilesUploaded, isGettingDataFilesUploaded } = useDataImport()
  const {
    getProjectQueries,
    isLoadingQueries,
    deleteQuery,
    saveStructured,
  } = useSavedQueries()
  const { pollingAsync, getAsyncResult } = useAsync()

  // Core state
  const [result, setResult] = useState<EngineQueryResult>({})
  const [isRunningScript, setIsRunningScript] = useState<boolean>(false)
  const [isCodebookLoading, setIsCodebookLoading] = useState<boolean>(false)
  const [dataUploaded, setDataUploaded] = useState<boolean>(false)
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true)
  const [selectedQuery, setSelectedQuery] = useState<string>("")
  const [codebook, setCodebook] = useState<CodebookData>([])
  const [queryInstance, setQueryInstance] = useState<QueryInstance | null>(null)
  const [projectQueries, setProjectQueries] = useState<string[]>([])
  const [contentChanged, setContentChanged] = useState<boolean>(false)

  // Holds the project id an engine query instance was requested for. Guards
  // against duplicate initialise-data calls (StrictMode double effects,
  // re-renders): every duplicate creates another engine instance holding a
  // full copy of the project's dataset, which lingers server-side.
  const initialisedProjectRef = useRef<string | null>(null)

  // Size of the DQD file the current engine instance was initialised from,
  // and the previously seen tab key. Together these detect "user came back
  // to this tab and the data changed since we initialised" — the only case
  // where a re-initialise is worth creating a new engine instance for.
  const dataFileSizeRef = useRef<number | null>(null)
  const prevActiveKeyRef = useRef<string | undefined>(activeKey)

  // Layout state — persisted via Zustand
  const viewMode = useQueryConsoleLayoutStore((s) => s.viewMode)
  const setViewMode = useQueryConsoleLayoutStore((s) => s.setViewMode)
  const dataTreeVisible = useQueryConsoleLayoutStore((s) => s.dataTreeVisible)
  const consoleOutputDirection = useQueryConsoleLayoutStore(
    (s) => s.consoleOutputDirection
  )
  const consoleOutputOrder = useQueryConsoleLayoutStore(
    (s) => s.consoleOutputOrder
  )
  const horizontalSplitSizes = useQueryConsoleLayoutStore(
    (s) => s.horizontalSplitSizes
  )
  const setHorizontalSplitSizes = useQueryConsoleLayoutStore(
    (s) => s.setHorizontalSplitSizes
  )
  const consoleSplitSizes = useQueryConsoleLayoutStore(
    (s) => s.consoleSplitSizes
  )
  const setConsoleSplitSizes = useQueryConsoleLayoutStore(
    (s) => s.setConsoleSplitSizes
  )
  const isMaximized = useQueryConsoleLayoutStore((s) => s.isMaximized)
  const setMaximized = useQueryConsoleLayoutStore((s) => s.setMaximized)
  const consoleStructuredOrigin = useQueryConsoleLayoutStore(
    (s) => s.consoleStructuredOrigin
  )
  const setConsoleStructuredOrigin = useQueryConsoleLayoutStore(
    (s) => s.setConsoleStructuredOrigin
  )

  // Builder-derived script. Always evaluated, but only used when viewMode is
  // "builder". Cheap memoization inside the hook.
  const { script: builderScript, runnable: builderRunnable } = useBuilderScript()
  // "Run all" — concatenated script for every runnable builder, plus the
  // ordered builder ids that contributed an OUTPUT line. Used by `runBatch`
  // below; `runnableCount` drives the toolbar button's enabled state.
  const {
    script: batchScript,
    includedBuilderIds: batchBuilderIds,
    runnableCount: batchRunnableCount,
  } = useBatchBuilderScript()
  const syncBuilderToProject = useQueryBuilderStore((s) => s.syncToProject)
  const { hydrateFromBackend, savePipeline, saveBuilder } = useTransformBackend()

  // Bind the persisted Builder draft to this project. If the persisted draft
  // is from a different project, this resets it so stale variables can't
  // leak across projects. Then hydrate any pipelines/builders persisted to
  // the backend (and migrate localStorage-only ones up on first visit).
  useEffect(() => {
    if (!project?.id) return
    syncBuilderToProject(project.id)
    if (project.Created_by) {
      hydrateFromBackend(project.id, project.Created_by).catch((err) => {
        console.warn("Failed to hydrate transform backend state:", err)
      })
    }
    // Hydrate intentionally not in deps — it's stable per project, and
    // re-running on every render would thrash the listStructured fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, project?.Created_by, syncBuilderToProject])

  // Initialize editor
  const editor = useEditor({
    extensions: [
      Document,
      Text,
      Paragraph,
      History,
      SmartClipboardExtension,
      CommandSuggestion,
      QuerySyntaxHighlight,
      QueryIndent,
      Placeholder.configure({
        placeholder:
          "Start typing your query, or type / to see available commands.",
      }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "console-font",
        // Query keywords/variable names aren't English words — browser
        // spell-check just litters the editor with red squiggles.
        spellcheck: "false",
      },
    },
  })

  // Share RunEngineQuery and queryInstance with CommandSuggestion extension
  // (anti-pattern but necessary for that extension to access these)
  useEffect(() => {
    window.RunEngineQuery = RunEngineQuery
    window.queryInstance = queryInstance
  }, [RunEngineQuery, queryInstance])

  // Derived state
  const pageLoading: boolean =
    !editor ||
    isGettingDataFilesUploaded ||
    isInitialisingData ||
    isLoadingQueries ||
    isDataLoading
  const scriptInProgress: boolean = isRunningEngineQuery && isRunningScript
  const asyncScriptInProgress: boolean =
    !isRunningEngineQuery && isRunningScript

  useEffect(() => {
    getInitialData()
    fetchQueries()
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When the user returns to this tab (e.g. after re-importing data in the
  // Data Import tab), re-initialise — but only if the DQD file actually
  // changed. A blind reload on every visit would create (and leak) another
  // engine query instance holding a full copy of the dataset each time.
  useEffect(() => {
    const cameBack =
      activeKey === DATA_REPORTING_TAB_KEY &&
      prevActiveKeyRef.current !== DATA_REPORTING_TAB_KEY
    prevActiveKeyRef.current = activeKey
    if (!cameBack || !project?.id || isDataLoading) return

    const reloadIfDataChanged = async (): Promise<void> => {
      try {
        const fetchRes = await getDataFilesUploaded(
          project.id,
          project.Created_by,
          DATAFILE_TYPE.DQD,
          "size"
        )
        const size = typeof fetchRes.data === "number" ? fetchRes.data : null
        if (size !== null && size !== dataFileSizeRef.current) {
          // Data changed since this instance was initialised: release the
          // duplicate guard and load fresh.
          initialisedProjectRef.current = null
          setIsDataLoading(true)
          await getInitialData()
        }
      } catch (error) {
        console.error("Error checking for data changes:", error)
      }
    }
    void reloadIfDataChanged()
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey])

  useEffect(() => {
    if (editor) {
      const updateContentChanged = (): void => {
        setContentChanged(true)
      }

      editor.on("update", updateContentChanged)
      return () => {
        editor.off("update", updateContentChanged)
      }
    }
  }, [editor])

  // Esc exits maximize mode. Only attached while maximized so we don't
  // swallow Esc for other contexts (modals, dropdowns).
  useEffect(() => {
    if (!isMaximized) return
    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMaximized(false)
    }
    document.addEventListener("keydown", handleEsc)
    return () => document.removeEventListener("keydown", handleEsc)
  }, [isMaximized, setMaximized])

  const getInitialData = async (): Promise<void> => {
    if (!project?.id || initialisedProjectRef.current === project.id) {
      // Already initialised (or initialising) for this project — a duplicate
      // call would create another engine query instance.
      setIsDataLoading(false)
      return
    }
    initialisedProjectRef.current = project.id
    try {
      const fetchRes = await getDataFilesUploaded(
        project.id,
        project.Created_by,
        DATAFILE_TYPE.DQD,
        "size"
      )

      if (typeof fetchRes.data === "number") {
        dataFileSizeRef.current = fetchRes.data
        const initialiseRes = await initialiseData(project.id)
        if (
          !handleSequenceError(initialiseRes.data, "Error Initializing Data!")
        ) {
          // No instance was created — allow a later retry.
          initialisedProjectRef.current = null
        } else {
          const instanceId = initialiseRes.data as QueryInstance
          setQueryInstance(instanceId)
          setIsCodebookLoading(true)
          const codebookRes = await RunEngineQuery(
            instanceId,
            "CODEBOOK",
            false
          )
          if (
            handleSequenceError(codebookRes.data, "Error Creating Codebook!")
          ) {
            const codebookData = codebookRes.data as EngineQueryResult
            if (codebookData.result?.output?.[0]?.content) {
              const outputItem = codebookData.result.output[0]
              if (outputItem.content && "value" in outputItem.content) {
                setCodebook(outputItem.content.value as CodebookData)
                setDataUploaded(true)
              }
            }
          }
          setIsCodebookLoading(false)
        }
      } else {
        // No data file uploaded yet — no instance was created, so a later
        // call (e.g. after an upload) may initialise.
        initialisedProjectRef.current = null
      }
    } catch (fetchError) {
      initialisedProjectRef.current = null
      console.error("Error fetching data:", fetchError)
    } finally {
      setIsDataLoading(false)
    }
  }

  const regenerateCodebook = useCallback(async (): Promise<void> => {
    if (!queryInstance) {
      // Project not yet initialised; nothing to refresh against.
      return
    }
    try {
      setIsCodebookLoading(true)
      const codebookRes = await RunEngineQuery(
        queryInstance,
        "CODEBOOK",
        false
      )
      if (handleSequenceError(codebookRes.data, "Error Creating Codebook!")) {
        const codebookData = codebookRes.data as EngineQueryResult
        if (codebookData.result?.output?.[0]?.content) {
          const outputItem = codebookData.result.output[0]
          if (outputItem.content && "value" in outputItem.content) {
            setCodebook(outputItem.content.value as CodebookData)
          }
        }
      }
      setIsCodebookLoading(false)
    } catch (error) {
      console.error("Error regenerating codebook:", error)
      setIsCodebookLoading(false)
    }
  }, [RunEngineQuery, queryInstance])

  const fetchQueries = useCallback(async (): Promise<void> => {
    try {
      const queryRes = await getProjectQueries(project.id, project.Created_by)
      if (queryRes.data) {
        const queryData: string[] = queryRes.data.toString().split("\n")
        setProjectQueries(queryData)
      } else {
        setProjectQueries([])
      }
    } catch (error) {
      console.error("Error fetching queries:", error)
    }
  }, [getProjectQueries, project.id, project.Created_by])

  /**
   * Returns the script to run for the current view. In Builder mode this is
   * the generated XTAB/DTAB; in Console mode the editor text.
   */
  const getCurrentScriptForRun = (): string => {
    if (viewMode === "builder") return builderScript
    if (!editor) return ""
    return editor.getText().replace(/\n\n+/g, "\n")
  }

  const runScript = async (): Promise<void> => {
    const script = getCurrentScriptForRun()
    if (!script.trim()) return

    // Snapshot where this run originated so the async result lands in the
    // correct place even if the user switches builders or view modes
    // before the engine responds.
    const runOriginIsBuilder = viewMode === "builder"
    const builderTargetId = runOriginIsBuilder
      ? useQueryBuilderStore.getState().activeBuilderId
      : null
    const setBuilderResult =
      useQueryBuilderStore.getState().setBuilderResult

    const applyResult = (data: EngineQueryResult): void => {
      if (runOriginIsBuilder && builderTargetId) {
        setBuilderResult(builderTargetId, data)
      } else {
        setResult(data)
      }
      // Codebook refresh is intentionally NOT auto-triggered here —
      // researchers control it explicitly via Transform → Apply or the
      // DataTree refresh button, which avoids cascading 502s when the
      // session is mid-flight.
    }

    try {
      setIsRunningScript(true)
      const result = await RunEngineQuery(
        queryInstance as QueryInstance,
        script,
        true
      )

      if (result.status === 202) {
        pollForAsyncResult(result.data as string, applyResult)
      } else {
        applyResult(result.data as EngineQueryResult)
        setIsRunningScript(false)
      }
    } catch (error) {
      console.error("An error occurred while running the script:", error)
      setIsRunningScript(false)
    }
  }

  /**
   * Run just the project-level custom variable definitions. Doesn't produce
   * an output — the goal is to materialise the FIELDs in the engine session
   * so the codebook (and hence the data tree) reflects them.
   */
  const runTransforms = async (): Promise<void> => {
    if (!queryInstance) {
      console.warn("Cannot apply transforms: project not yet initialised")
      return
    }
    const { transformPipelines, activeTransformPipelineId } =
      useQueryBuilderStore.getState()
    const pipeline = transformPipelines.find(
      (p) => p.id === activeTransformPipelineId
    )
    const prefix = pipeline ? buildTransformPrefix(pipeline.steps) : ""
    if (!prefix) return

    const pipelineName = pipeline?.name ?? "pipeline"

    try {
      setIsRunningScript(true)
      // RESET so the run starts from a clean session; otherwise prior
      // state could clash with redefining a recode name.
      const script = `RESET\n${prefix}`
      const response = await RunEngineQuery(queryInstance, script, false)

      // The engine returns 200 even on script errors — the failure shows up
      // in `Err code` and as a non-success entry in `result.log`. Treat any
      // log line that isn't the literal "Script successfully executed." as
      // an error and surface the engine's own message to the user.
      const body = response.data as {
        "Err code"?: number
        result?: { log?: string[] }
      } | null
      const errCode = body?.["Err code"] ?? 0
      const logLines = body?.result?.log ?? []
      const lastLog = logLines[logLines.length - 1] ?? ""
      const succeeded =
        errCode === 0 && lastLog === "Script successfully executed."

      if (succeeded) {
        await regenerateCodebook()
        notify.success(
          "Transform applied",
          `"${pipelineName}" ran successfully.`
        )
      } else {
        // Prefer the engine's last log line for the description — it's the
        // line that actually tells the user *why* the run failed (e.g.
        // "QTable already exists: <<TITLE>>"). Fall back to the err code
        // when the log is empty.
        const detail =
          lastLog || `Engine returned Err code ${errCode}.`
        notify.error("Transform failed", detail)
      }
    } catch (error) {
      console.error("Error running transforms:", error)
      notify.error(
        "Transform failed",
        error instanceof Error ? error.message : "Unknown error"
      )
    } finally {
      setIsRunningScript(false)
    }
  }

  /**
   * "Run all" — concatenate every runnable Builder's script into one engine
   * query (single RESET, each Transform pipeline emitted once even when
   * multiple Builders share it) and stash the multi-output response in the
   * store via `setBatchResult`. The per-builder Apply path stays untouched.
   */
  const runBatch = useCallback(async (): Promise<void> => {
    if (!queryInstance) {
      console.warn("Cannot run batch: project not yet initialised")
      return
    }
    if (batchRunnableCount === 0 || !batchScript.trim()) {
      notify.error(
        "Nothing to run",
        "No Builders have both rows and columns yet."
      )
      return
    }
    const setBatchResult = useQueryBuilderStore.getState().setBatchResult
    const includedIdsSnapshot = [...batchBuilderIds]

    try {
      setIsRunningScript(true)
      const response = await RunEngineQuery(queryInstance, batchScript, false)

      // Match the success/error convention used by runTransforms — engine
      // returns 200 even on script errors, signalled via `Err code` and the
      // last `result.log` line.
      const body = response.data as
        | {
            "Err code"?: number
            result?: { log?: string[] }
          }
        | null
      const errCode = body?.["Err code"] ?? 0
      const logLines = body?.result?.log ?? []
      const lastLog = logLines[logLines.length - 1] ?? ""
      const succeeded =
        errCode === 0 && lastLog === "Script successfully executed."

      if (succeeded) {
        setBatchResult(
          response.data as EngineQueryResult,
          includedIdsSnapshot
        )
        await regenerateCodebook()
        notify.success(
          "Batch ran",
          `${includedIdsSnapshot.length} table${
            includedIdsSnapshot.length === 1 ? "" : "s"
          } produced.`
        )
      } else {
        const detail = lastLog || `Engine returned Err code ${errCode}.`
        notify.error("Batch run failed", detail)
      }
    } catch (error) {
      console.error("Error running batch:", error)
      notify.error(
        "Batch run failed",
        error instanceof Error ? error.message : "Unknown error"
      )
    } finally {
      setIsRunningScript(false)
    }
  }, [
    queryInstance,
    batchScript,
    batchBuilderIds,
    batchRunnableCount,
    RunEngineQuery,
    regenerateCodebook,
  ])

  /**
   * Walk the most recent batch result and write each OUTPUT to its own
   * sheet (mode === "separate") or stack them all into one sheet with
   * title rows between them (mode === "combined") — matches the Reporting
   * Dashboard's Excel-export UX.
   *
   * Sheet/title names come from the originating builder's user-facing
   * `name` (not the technical `outputName`); falls back to the engine's
   * OUTPUT label when a builder can't be matched by index.
   */
  const exportBatchToExcel = useCallback(
    async (mode: "separate" | "combined" = "separate"): Promise<void> => {
      const state = useQueryBuilderStore.getState()
      const batchResult = state.batchRunResult
      const builderIds = state.batchRunBuilderIds
      const outputs = batchResult?.result?.output ?? []
      if (outputs.length === 0) {
        notify.error(
          "Nothing to export",
          "Run the batch first — there are no results to write."
        )
        return
      }
      // Collect per-output `{ name, data }`. Each output's `content` is an
      // `RTableContent` whose `data` is already an array-of-arrays — the
      // exact shape `exportToExcel` wants.
      const tables: {
        name: string
        data: (string | number | boolean | Date | null)[][]
      }[] = []
      outputs.forEach((item, idx) => {
        const content = item.content as RTableContent | undefined
        if (!content || !Array.isArray(content.data)) return
        // Prefer the user-facing Builder name. The engine's OUTPUT label
        // equals the builder's `outputName`; match by index first
        // (script-emit order = output order) and fall back to engine label.
        const builderId = builderIds[idx]
        const builder = builderId
          ? state.builders.find((b) => b.id === builderId)
          : undefined
        tables.push({
          name: builder?.name || item.label || `Table ${idx + 1}`,
          data: content.data as (string | number | boolean | Date | null)[][],
        })
      })
      if (tables.length === 0) {
        notify.error(
          "Nothing to export",
          "The batch result has no tabular outputs."
        )
        return
      }

      const projectName =
        project?.Name?.replace(/[^\w-]+/g, "_") || "Builder"
      const timestamp = new Date().toISOString().split("T")[0]

      if (mode === "separate") {
        await exportToExcel(
          tables.map((t) => t.data),
          `${projectName}_builder_batch_${timestamp}`,
          { sheetNames: tables.map((t) => t.name) }
        )
        return
      }

      // Combined mode — stack tables into a single AOA with a title row
      // before each and a two-row gap between successive tables. Mirrors
      // `ReportingMenuBar.component.tsx`'s combined-export shape exactly.
      const merged: (string | number | boolean | Date | null)[][] = []
      tables.forEach((table, idx) => {
        if (idx > 0) {
          merged.push([])
          merged.push([])
        }
        merged.push([table.name])
        table.data.forEach((row) => merged.push(row))
      })
      await exportToExcel(
        merged,
        `${projectName}_builder_batch_combined_${timestamp}`,
        { sheetName: "All Tables" }
      )
    },
    [project?.Name]
  )

  const pollForAsyncResult = async (
    asyncID: string,
    applyResult: (data: EngineQueryResult) => void
  ): Promise<void> => {
    try {
      const pollResult = await pollingAsync(asyncID)
      if (pollResult.status === 200) {
        const asyncResult = await getAsyncResult(asyncID)
        React.startTransition(() => {
          applyResult(asyncResult.data as EngineQueryResult)
          setIsRunningScript(false)
        })
      } else {
        setTimeout(() => pollForAsyncResult(asyncID, applyResult), 5000)
      }
    } catch (error) {
      console.error("Polling error:", error)
      setIsRunningScript(false)
    }
  }

  /**
   * Returns the script to save. Builder mode emits the generated script
   * directly; Console mode strips TipTap HTML back to plain text.
   */
  const getCurrentScriptForSave = (): string => {
    if (viewMode === "builder") return builderScript
    if (!editor) return ""
    return editor
      .getHTML()
      .replace(/<p>/g, "")
      .replace(/<\/p>/g, "\n")
      .replace(/&lt;&lt;/g, "<<")
      .replace(/&gt;&gt;/g, ">>")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      .trim()
  }

  const saveProjectQuery = async (queryName: string): Promise<void> => {
    const query = getCurrentScriptForSave()
    if (!query.trim()) return

    try {
      // Use the structured save path so scripts also write a script-kind
      // sidecar. Critical when the user has just "Edit anyway"-ed a pipeline
      // or builder into Console — saving under a fresh name has to leave a
      // sidecar that explicitly says `kind: "script"`, otherwise the
      // bulk-meta-list would still classify the new record by whatever
      // sidecar exists on disk.
      await saveStructured(
        project.id,
        project.Created_by,
        queryName,
        query,
        { kind: "script" }
      )
      React.startTransition(() => {
        setContentChanged(false)
        fetchQueries()
        // Picking up where the user is: clear the structured-origin marker
        // so the next "Run/Save" cycle treats the editor as a regular new
        // script (no banner, no forced save-as).
        setConsoleStructuredOrigin(null)
        setSelectedQuery(queryName)
        notify.success(
          "Query saved successfully!",
          "View Saved Queries to access your saved queries."
        )
      })
    } catch (error) {
      console.error("Error saving query:", error)
    }
  }

  const handleConsoleSplitChange = useCallback(
    (sizes: number[]): void => {
      setConsoleSplitSizes(sizes)
    },
    [setConsoleSplitSizes]
  )

  // Leaving Console-as-viewer for the structured record's native tab.
  // Clearing the editor + selectedQuery here is critical: TipTap is
  // persistent across viewMode switches, so without these resets the script
  // would still be sitting in the editor when the user returns to Console,
  // and selectedQuery would still point at the pipeline — meaning a Save
  // would silently overwrite the pipeline's body with a flat script.
  const handleOpenStructuredInTab = useCallback(
    (kind: "pipeline" | "builder"): void => {
      if (editor) {
        editor.commands.clearContent()
      }
      setSelectedQuery("")
      setContentChanged(false)
      setConsoleStructuredOrigin(null)
      setViewMode(kind === "pipeline" ? "transform" : "builder")
    },
    [editor, setViewMode, setConsoleStructuredOrigin]
  )

  const consoleComponent = useMemo(
    () => (
      <Console
        editor={editor}
        result={result}
        scriptInProgress={scriptInProgress}
        asyncScriptInProgress={asyncScriptInProgress}
        splitSizes={consoleSplitSizes}
        onSplitChange={handleConsoleSplitChange}
        direction={consoleOutputDirection}
        order={consoleOutputOrder}
        onOpenStructuredInTab={handleOpenStructuredInTab}
      />
    ),
    [
      editor,
      result,
      scriptInProgress,
      asyncScriptInProgress,
      consoleSplitSizes,
      handleConsoleSplitChange,
      handleOpenStructuredInTab,
      consoleOutputDirection,
      consoleOutputOrder,
    ]
  )

  const handleSaveActiveBuilder = useCallback(
    async (saveName: string): Promise<void> => {
      if (!project?.id || !project.Created_by) return
      const activeId = useQueryBuilderStore.getState().activeBuilderId
      if (!activeId) return
      try {
        await saveBuilder(project.id, project.Created_by, activeId, saveName)
        notify.success(
          "Builder saved",
          `"${saveName}" was saved to the project.`
        )
        fetchQueries()
      } catch (err) {
        console.error("Failed to save builder:", err)
      }
    },
    [project?.id, project?.Created_by, saveBuilder, fetchQueries]
  )

  const handleDeleteActiveBuilder = useCallback(
    async (): Promise<void> => {
      const state = useQueryBuilderStore.getState()
      const activeId = state.activeBuilderId
      if (!activeId) return
      const savedName = state.builderSavedNames[activeId]
      state.removeBuilder(activeId)
      if (savedName && project?.id && project.Created_by) {
        try {
          await deleteQuery(project.id, project.Created_by, savedName)
          fetchQueries()
        } catch (err) {
          console.error(
            "Builder removed locally but backend delete failed:",
            err
          )
        }
      }
    },
    [project?.id, project?.Created_by, deleteQuery, fetchQueries]
  )

  const builderComponent = useMemo(
    () => (
      <QueryBuilder
        scriptInProgress={scriptInProgress}
        asyncScriptInProgress={asyncScriptInProgress}
        splitSizes={consoleSplitSizes}
        onSplitChange={handleConsoleSplitChange}
        direction={consoleOutputDirection}
        order={consoleOutputOrder}
        onSaveActiveBuilder={handleSaveActiveBuilder}
        onDeleteActiveBuilder={handleDeleteActiveBuilder}
        onRunBatch={runBatch}
        onExportBatch={exportBatchToExcel}
        batchRunnableCount={batchRunnableCount}
      />
    ),
    [
      scriptInProgress,
      asyncScriptInProgress,
      consoleSplitSizes,
      handleConsoleSplitChange,
      consoleOutputDirection,
      consoleOutputOrder,
      handleSaveActiveBuilder,
      handleDeleteActiveBuilder,
      runBatch,
      exportBatchToExcel,
      batchRunnableCount,
    ]
  )

  const savedQueriesComponent = useMemo(
    () => (
      <SavedQueries
        project={project}
        toggleSaved={() => setViewMode("console")}
        projectQueries={projectQueries}
        editor={editor}
        refreshQueries={fetchQueries}
        setSelectedQuery={setSelectedQuery}
        selectedQuery={selectedQuery}
        setContentChanged={setContentChanged}
      />
    ),
    [
      project,
      projectQueries,
      editor,
      selectedQuery,
      setSelectedQuery,
      setContentChanged,
      setViewMode,
      fetchQueries,
    ]
  )

  // No useMemo here: runTransforms closes over queryInstance, which is set
  // asynchronously by getInitialData. Memoising on isRunningScript captured
  // a stale runTransforms with queryInstance=null until the first Builder
  // run forced the memo to refresh. Re-creating the JSX every render is
  // cheap and TransformView reads its own state from the store.
  const handleSaveActivePipeline = useCallback(
    async (saveName: string): Promise<void> => {
      if (!project?.id || !project.Created_by) return
      const activeId =
        useQueryBuilderStore.getState().activeTransformPipelineId
      if (!activeId) return
      try {
        await savePipeline(project.id, project.Created_by, activeId, saveName)
        notify.success(
          "Pipeline saved",
          `"${saveName}" was saved to the project.`
        )
        // Refresh queries so the SavedQueries list shows the new entry.
        fetchQueries()
      } catch (err) {
        console.error("Failed to save pipeline:", err)
      }
    },
    [project?.id, project?.Created_by, savePipeline, fetchQueries]
  )

  const handleDeleteActivePipeline = useCallback(
    async (): Promise<void> => {
      const state = useQueryBuilderStore.getState()
      const activeId = state.activeTransformPipelineId
      if (!activeId) return
      // Capture the saved name BEFORE removing the local entry — the store
      // wipes `pipelineSavedNames[activeId]` as part of removeTransformPipeline.
      const savedName = state.pipelineSavedNames[activeId]
      state.removeTransformPipeline(activeId)
      if (savedName && project?.id && project.Created_by) {
        try {
          await deleteQuery(project.id, project.Created_by, savedName)
          fetchQueries()
        } catch (err) {
          console.error(
            "Pipeline removed locally but backend delete failed:",
            err
          )
        }
      }
    },
    [project?.id, project?.Created_by, deleteQuery, fetchQueries]
  )

  const transformComponent = (
    <TransformView
      onApply={runTransforms}
      isApplying={isRunningScript}
      onSaveActivePipeline={handleSaveActivePipeline}
      onDeleteActivePipeline={handleDeleteActivePipeline}
      codebook={codebook}
    />
  )

  // Plain value, not memoised: transformComponent is intentionally re-
  // created each render so it has a fresh closure over queryInstance, so
  // any memo here would invalidate every render anyway.
  let rightPaneContent: React.ReactNode
  switch (viewMode) {
    case "builder":
      rightPaneContent = builderComponent
      break
    case "transform":
      rightPaneContent = transformComponent
      break
    case "saved":
      rightPaneContent = savedQueriesComponent
      break
    case "console":
    default:
      rightPaneContent = consoleComponent
  }

  const dataTreePane = (
    <div className="w-1/4 border flex flex-col overflow-hidden">
      <DataTree
        editor={editor}
        codebook={codebook}
        isCodebookLoading={isCodebookLoading}
        regenerateCodebook={regenerateCodebook}
      />
    </div>
  )

  const rightPane = (
    <div className="flex-1 flex flex-col overflow-hidden">
      {rightPaneContent}
    </div>
  )

  const mainSplitView = useMemo(() => {
    if (!dataTreeVisible) {
      return <div className="flex flex-grow overflow-hidden">{rightPane}</div>
    }

    return (
      <Split
        className="flex flex-grow overflow-hidden"
        direction="horizontal"
        gutterSize={6}
        sizes={horizontalSplitSizes}
        onDragEnd={setHorizontalSplitSizes}
        minSize={100}
      >
        {dataTreePane}
        {rightPane}
      </Split>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataTreeVisible,
    horizontalSplitSizes,
    setHorizontalSplitSizes,
    editor,
    codebook,
    isCodebookLoading,
    regenerateCodebook,
    rightPaneContent,
  ])

  if (pageLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader fontSize={48} color="#36C3ED" />
        {!editor
          ? "Loading Editor..."
          : isGettingDataFilesUploaded
          ? "Checking Data File..."
          : isInitialisingData
          ? "Initializing Data File..."
          : "Loading"}
      </div>
    )
  }

  if (!isDataLoading && !dataUploaded) {
    return (
      <EmptyState message="No Data File Found! Please go to Data Import tab and import/generate the data file" />
    )
  }

  return (
    <ConfigProvider theme={QC_THEME}>
      <div
        className="query-console-root flex flex-col h-full"
        data-maximized={isMaximized || undefined}
      >
        <div className="qc-action-row flex flex-row items-center p-2 gap-3">
          <QueryConsoleViewMode />
          <QueryConsoleHelp />
          <QueryStatus
            projectQueries={projectQueries}
            selectedQuery={selectedQuery}
            editor={editor}
            setContentChanged={setContentChanged}
            setSelectedQuery={setSelectedQuery}
          />
          <Tooltip
            title={
              viewMode === "saved"
                ? "Back to editor"
                : "Browse saved queries"
            }
            placement="bottom"
          >
            <button
              type="button"
              className="qc-saved-toggle"
              aria-label="Saved queries"
              aria-pressed={viewMode === "saved"}
              onClick={() =>
                setViewMode(viewMode === "saved" ? "console" : "saved")
              }
            >
              <FontAwesomeIcon icon={faHardDrive} />
            </button>
          </Tooltip>
          <div className="flex ml-auto items-center space-x-2">
            {viewMode === "console" && (
              <Tooltip
                title="Format script — re-indent FOR/IF blocks (Shift + Alt + F)"
                placement="bottom"
              >
                <button
                  type="button"
                  className="qc-saved-toggle"
                  aria-label="Format script"
                  onClick={() => editor?.commands.formatQueryScript()}
                >
                  <FontAwesomeIcon icon={faIndent} />
                </button>
              </Tooltip>
            )}
            <QueryConsoleLayoutControls />
            <span className="qc-action-divider" aria-hidden />
            <QueryConsoleActions
              queryInstance={queryInstance}
              runScript={runScript}
              isRunningScript={isRunningScript}
              canRun={viewMode === "builder" ? builderRunnable : true}
              saveProjectQuery={saveProjectQuery}
              selectedQuery={selectedQuery}
              setSelectedQuery={setSelectedQuery}
              contentChanged={contentChanged}
              activeKey={activeKey}
              // Transform has its own Apply (in the Transform header); Saved
              // is a list view with no editor — Run is meaningless in both.
              hideRun={viewMode === "transform" || viewMode === "saved"}
              // Builder + Transform have kind-aware Save in their own
              // toolbars; the action-row Save is only useful for Console.
              hideSave={viewMode !== "console"}
              // When the editor is showing a structured record's script,
              // force Save to "Save as" so the original pipeline/builder
              // sidecar isn't clobbered with a script-kind one.
              forceSaveAs={consoleStructuredOrigin !== null}
            />
          </div>
        </div>
        {mainSplitView}
      </div>
    </ConfigProvider>
  )
}

export default QueryConsoleContainer
